import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import type { UserRole } from "../../types";
import { isDirectorateScoped, myDirectorateReportPath } from "../../lib/directorateScope";
import { formatDate, formatDateLong, todayISO } from "../../lib/date";

const NAV_LINKS: Array<{ to: string; label: string; roles?: UserRole[] }> = [
  { to: "/",                     label: "🏠 الرئيسية" },
  { to: "/dashboard",             label: "📊 لوحة المعلومات" },
  { to: "/meal-entry",           label: "📝 طلب وجبات",  roles: ["DATA_ENTRY", "REVIEWER", "SYSTEM_ADMIN"] },
  { to: "/all-data",             label: "📋 كل الطلبات" },
  { to: "/receipt",              label: "📦 الاستلام" },
  { to: "/summary/directorates", label: "🏛 ملخص المديريات" },
  { to: "/summary/governorates", label: "🗺 ملخص المحافظات" },
  { to: "/headcount",            label: "👥 أعداد الكيانات", roles: ["REVIEWER", "SYSTEM_ADMIN"] },
  { to: "/dictionaries",         label: "📚 القواميس",    roles: ["SYSTEM_ADMIN"] },
  { to: "/users",                label: "👤 المستخدمون",  roles: ["SYSTEM_ADMIN"] },
  { to: "/audit",                label: "🔍 التدقيق",     roles: ["SYSTEM_ADMIN"] },
  { to: "/settings",             label: "⚙️ الإعدادات",   roles: ["SYSTEM_ADMIN"] },
];

export const COPYRIGHT = `© ${new Date().getFullYear()} دائرة المعلوماتية — وزارة الدفاع. جميع الحقوق محفوظة`;

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, isRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const scoped = isDirectorateScoped(user);

  // تنبيه الاستلام — لمدخل بيانات الفرع فقط، والخادم هو من يحدد ما ينتظر
  const isReceiver = user?.role === "DATA_ENTRY" && user.directorateId != null;
  const { data: pending = [] } = useQuery({
    queryKey: ["pending-receipts"],
    enabled: isReceiver,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: () => api.get<{ data: Array<{ id: number; entryDate: string }> }>("/meal-entries/pending-receipts")
                      .then((r) => r.data.data),
  });
  const showReceiptBanner = isReceiver && pending.length > 0 && !location.pathname.startsWith("/receipt");
  const reportPath = myDirectorateReportPath(user);

  const visibleLinks = NAV_LINKS.filter((l) => {
    if (l.roles && !isRole(...l.roles)) return false;
    if (scoped && (l.to === "/summary/directorates" || l.to === "/summary/governorates")) return false;
    return true;
  });

  const navLinks = reportPath
    ? [...visibleLinks.slice(0, 4), { to: reportPath, label: "🏛 ما طلبته مديريتي" }, ...visibleLinks.slice(4)]
    : visibleLinks;

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <div className="min-h-screen flex flex-col">
      {/* ─── الترويسة ─── */}
      <header className="no-print sticky top-0 z-50 bg-white border-b border-sand-300 h-16 px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-sand-100 flex items-center justify-center">
            <img src="/logo.png" alt="" className="w-7 h-7 object-contain" />
          </div>
          <div className="leading-tight">
            <h2 className="text-base font-bold text-gray-900">طلبات وجبات الطعام</h2>
            <span className="block text-xs text-sand-500">الإدارة العامة — فرع الشؤون الإدارية</span>
          </div>
        </Link>

        {user && (
          <div className="flex items-center gap-3">
            <span className="hidden md:block text-[11px] text-sand-500">{formatDateLong(todayISO())}</span>
            <div className="hidden sm:flex items-center gap-2 rounded-lg border border-sand-300 bg-sand-50 px-3 py-1.5 text-[13px] text-sand-700">
              <span className="w-6 h-6 rounded-full bg-primary-700 text-white text-[11px] font-bold flex items-center justify-center">
                {user.fullName.trim().charAt(0)}
              </span>
              <span>{user.fullName}</span>
            </div>
            <Link to="/profile" className="icon-btn">🔑 <span className="hidden sm:inline">حسابي</span></Link>
            <button onClick={handleLogout} className="icon-btn">🚪 <span className="hidden sm:inline">خروج</span></button>
          </div>
        )}
      </header>

      {/* ─── شريط التنقل ─── */}
      <nav className="no-print bg-white border-b border-sand-300 px-6 py-3">
        <div className="inline-flex flex-wrap gap-0.5 rounded-[10px] border border-sand-300 bg-sand-50 p-1">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-[7px] px-4 py-2 text-[13px] font-semibold transition ${
                isActive(link.to)
                  ? "bg-primary-700 text-white"
                  : "text-sand-500 hover:bg-sand-300 hover:text-gray-900"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </nav>

      {/* ─── تنبيه الاستلام ─── */}
      {showReceiptBanner && (
        <div className="no-print bg-amber-50 border-b border-amber-300 px-6 py-2 text-sm text-amber-800 flex flex-wrap items-center gap-2">
          <span>📦 لديك {pending.length === 1 ? "طلب" : `${pending.length} طلبات`} بانتظار تأكيد الاستلام
            ({pending.map((p) => formatDate(p.entryDate)).join("، ")})</span>
          <Link to={`/receipt?date=${pending[0]!.entryDate}`}
            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700">
            تأكيد الاستلام الآن
          </Link>
        </div>
      )}

      {/* ─── المحتوى ─── */}
      <main className="flex-1 container mx-auto px-4 py-6 max-w-7xl">
        {children}
      </main>

      {/* ─── التذييل ─── */}
      <footer className="no-print border-t border-sand-300 bg-white text-sand-500 text-xs text-center py-3">
        {COPYRIGHT}
      </footer>
    </div>
  );
}
